import { useLocation, useNavigate } from "react-router-dom"
import { BottomNavigation, BottomNavigationAction, Paper } from "@mui/material"
import MoreIcon from "@mui/icons-material/MoreHorizOutlined"

import { usePermissions } from "../permissions"
import { visibleNavSections } from "./navConfig"

/**
 * The four places somebody goes on a phone, along the bottom.
 *
 * ── Why the drawer was not enough ───────────────────────────────────────────
 *
 * On a phone the sidebar is behind a hamburger, which is two taps and a
 * full-screen overlay to reach a page — and the overlay covers what you were
 * reading, so it costs you your place as well. A bottom bar is one tap with the
 * thumb that is already holding the device.
 *
 * ── Why four, and why these four ────────────────────────────────────────────
 *
 * Five is the ceiling before the labels stop fitting, and the fifth is the
 * hamburger, which still reaches everything. The four are taken from the top of
 * the same permission-filtered list the sidebar uses, so a role that cannot see
 * Tasks does not get a Tasks tab that 403s — and nothing here is hardcoded to a
 * page that might one day be removed.
 */
export default function MobileNav({ onOpenMore }) {
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = usePermissions()

  const items = visibleNavSections(can)
    .flatMap((section) => section.items)
    .slice(0, 4)

  /* The longest matching path wins, so /projects/12 lights Projects rather
     than Dashboard, whose "/" is a prefix of everything. */
  const current = items
    .filter((item) => (item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to)))
    .sort((a, b) => b.to.length - a.to.length)[0]

  return (
    <Paper
      elevation={8}
      sx={{
        display: { xs: "block", md: "none" },
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: (theme) => theme.zIndex.appBar,
        /* The home indicator on a modern phone sits over the bottom 34px. */
        pb: "env(safe-area-inset-bottom)",
        borderRadius: 0,
      }}
    >
      <BottomNavigation value={current?.id ?? false} showLabels>
        {items.map((item) => (
          <BottomNavigationAction
            key={item.id}
            value={item.id}
            label={item.label}
            icon={<item.icon />}
            onClick={() => navigate(item.to)}
          />
        ))}
        <BottomNavigationAction label="More" icon={<MoreIcon />} onClick={onOpenMore} value="more" />
      </BottomNavigation>
    </Paper>
  )
}
